// Custom fields in the ticket list (#9399, #9764).
//
// One narrow Fields column, in a department view and in All items alike. A
// row with any field filled in shows an icon; hovering (or focusing) it opens
// a card with every field that applies to the row on its own line. The header
// sorts by any field and filters each the way the built-in headers do: option
// ticks for a pick-list or checkbox, a day range for a date, a from/to pair
// for a number. Text is found by the list search.
import { useState } from "react";
import { ListFilter, Tags, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ColorBadge from "@/components/shared/ColorBadge";
import {
  DateRangeSection,
  HeaderOptionList,
  HeaderTrigger,
  SortSection,
  type HeaderSortControl,
} from "@/components/backlog/HeaderFilter";
import { cn } from "@/lib/utils";
import {
  fieldFilterOptions,
  formatFieldValue,
  isFieldFilterSet,
  optionColor,
  optionOf,
  type CustomField,
  type CustomFieldValues,
  type FieldFilter,
} from "@/lib/customFields";

/** A row's Fields cell: an icon when any field has a value, every field on hover. */
export function FieldsCell({
  fields,
  values,
}: {
  /** The fields that apply to this row, in order. */
  fields: readonly CustomField[];
  values: CustomFieldValues;
}) {
  // Each row only asks whether anything is filled, stopping at the first; the
  // values are formatted when the hover card opens, so thousands of rows don't
  // format text nobody is looking at.
  const anyFilled = fields.some((f) => formatFieldValue(f, values[f.id]) !== null);
  return (
    <td className="hidden w-20 px-2 py-2 text-center xl:table-cell">
      {anyFilled && (
        <HoverCard openDelay={150} closeDelay={80}>
          <HoverCardTrigger asChild>
            <span
              tabIndex={0}
              role="img"
              aria-label="Custom fields"
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Tags className="h-4 w-4" />
            </span>
          </HoverCardTrigger>
          <HoverCardContent align="end" className="w-72 border-border bg-card p-3">
            <dl className="space-y-1.5 text-sm">
              {fields.map((field) => (
                <div key={field.id} className="flex items-baseline justify-between gap-3">
                  <dt className="shrink-0 text-xs font-semibold text-muted-foreground">{field.name}</dt>
                  <dd className="min-w-0 truncate text-right text-foreground">
                    <FieldValue field={field} value={values[field.id]} />
                  </dd>
                </div>
              ))}
            </dl>
          </HoverCardContent>
        </HoverCard>
      )}
    </td>
  );
}

/** A pick-list option as its coloured chip, anything else as text, an empty field as a dash. */
function FieldValue({ field, value }: { field: CustomField; value: unknown }) {
  const option = optionOf(field, value);
  if (option) return <ColorBadge label={option.label} color={optionColor(option)} shape="mini" />;
  return formatFieldValue(field, value) ?? <span className="text-muted-foreground">—</span>;
}

interface FieldsColumnHeaderProps {
  fields: readonly CustomField[];
  sortFor: (field: CustomField) => HeaderSortControl;
  filters: Record<string, FieldFilter>;
  onFilterChange: (fieldId: string, filter: FieldFilter | undefined) => void;
}

/** The Fields column's header: pick a field, then sort by it or filter it. */
export function FieldsColumnHeader({ fields, sortFor, filters, onFilterChange }: FieldsColumnHeaderProps) {
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState<string | null>(null);
  const activeFilters = fields.filter((f) => isFieldFilterSet(filters[f.id])).length;
  const sorted = fields.map((f) => sortFor(f)).filter((s) => s.index !== null);
  const first = sorted.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))[0];
  const current = fields.find((f) => f.id === focus) ?? fields[0];
  const filter = current ? filters[current.id] : undefined;
  const setFilter = (next: FieldFilter | undefined) => current && onFilterChange(current.id, next);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <HeaderTrigger
          label="Fields"
          title="Sort or filter by a custom field"
          filterCount={activeFilters}
          sortIndex={first?.index ?? null}
          sortDirection={first?.direction ?? null}
          restIcon={<ListFilter className="th-filter-icon h-3 w-3 shrink-0" />}
          className="justify-center"
        />
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-[75vh] w-64 overflow-y-auto border-border bg-card p-0">
        <div className="flex flex-wrap gap-1 border-b border-border p-2">
          {fields.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFocus(f.id)}
              className={cn(
                "rounded px-2 py-0.5 text-xs transition-colors",
                f.id === current?.id
                  ? "bg-[hsl(var(--ring)/0.15)] font-semibold text-brand"
                  : "text-foreground hover:bg-muted",
              )}
            >
              {f.name}
              {(isFieldFilterSet(filters[f.id]) || sortFor(f).index !== null) && (
                <span aria-hidden className="ml-1 text-brand">
                  •
                </span>
              )}
            </button>
          ))}
        </div>
        {current && (
          <>
            <SortSection sort={sortFor(current)} />
            {(current.field_type === "select" || current.field_type === "checkbox") && (
              <HeaderOptionList
                options={fieldFilterOptions(current)}
                selected={filter?.values ?? []}
                onChange={(values) => setFilter({ values })}
              />
            )}
            {current.field_type === "date" && (
              <DateRangeSection
                key={current.id}
                range={{ from: filter?.from ?? null, to: filter?.to ?? null }}
                onRangeChange={(range) => setFilter(range)}
              />
            )}
            {current.field_type === "number" && (
              <NumberRangeSection key={current.id} field={current} filter={filter} onFilterChange={setFilter} />
            )}
          </>
        )}
        {activeFilters > 0 && (
          <div className="border-t border-border p-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-full justify-start gap-1.5 px-2 text-xs text-muted-foreground"
              onClick={() => fields.forEach((f) => onFilterChange(f.id, undefined))}
            >
              <X className="h-3 w-3" />
              Clear field filters
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** A number field's from/to range. Keyed by field, so switching fields resets the inputs. */
function NumberRangeSection({
  field,
  filter,
  onFilterChange,
}: {
  field: CustomField;
  filter: FieldFilter | undefined;
  onFilterChange: (filter: FieldFilter | undefined) => void;
}) {
  const set = (end: "from" | "to", raw: string) => {
    const text = raw.trim();
    onFilterChange({ ...filter, [end]: text === "" || !Number.isFinite(Number(text)) ? null : text });
  };
  return (
    <div className="space-y-2 p-2">
      <p className="px-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Range</p>
      <div className="flex items-center gap-2">
        <Input
          aria-label={`${field.name} from`}
          inputMode="decimal"
          placeholder="From"
          defaultValue={filter?.from ?? ""}
          onChange={(e) => set("from", e.target.value)}
          className="h-8"
        />
        <Input
          aria-label={`${field.name} to`}
          inputMode="decimal"
          placeholder="To"
          defaultValue={filter?.to ?? ""}
          onChange={(e) => set("to", e.target.value)}
          className="h-8"
        />
      </div>
    </div>
  );
}
